import React, { useState, useEffect, useCallback } from "react";
import styles from "./CompanyList.module.sass";
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../components/Icon";
import { useNavigate } from "react-router-dom";
import { getCenters } from "../../services/center.service";

const CenterList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);

  const handleStatusChange = (id, newStatus) => {
    setCenters(prevCenters =>
      prevCenters.map(center =>
        center.id === id ? { ...center, status: newStatus } : center
      )
    );
  };

  const fetchCenters = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      const result = await getCenters({
        page,
        perPage: 20,
        search
      });

      setCenters(result.items);
      setTotalPages(result.totalPages);
      setNoResults(result.items.length === 0 && !!search);
    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Error loading centers:", err);
        setError("Une erreur est survenue lors du chargement des centres.");
        setCenters([]);
      }
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchCenters();
  }, [page, fetchCenters]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <Card
        className={styles.card}
        title="Centres de formation"
        classTitle={cn("title-blue", styles.title)}
        classCardHead={styles.head}
        head={
          <>
            <Form
              className={styles.form}
              value={search}
              setValue={setSearch}
              onSubmit={handleSearch}
              placeholder="Rechercher par nom"
              type="text"
              name="search"
              icon="search"
            />
            <button className={cn("button-stroke button-small", styles.button)} onClick={() => navigate("/admin/centers/create")}>
              Créer un centre
            </button>
          </>
        }
      >
        {error && <div className={styles.error}>{error}</div>}

        {loading ? (
          <div className={styles.loading}>Chargement...</div>
        ) : (
          <div className={styles.row}>
            {noResults && centers.length === 0 && (
              <div className={styles.empty}>
                Aucun centre trouvé pour votre recherche "{search}".
              </div>
            )}
            {!noResults && centers.length === 0 && !loading && !error && (
              <div className={styles.empty}>
                Aucun centre de formation disponible.
              </div>
            )}
            {centers.length > 0 && (
              <>
                <Table
                  className={styles.table}
                  items={centers}
                  onStatusChange={handleStatusChange}
                  onSelectionChange={setSelectedIds}
                />

                <div className={styles.foot}>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.max(1, page - 1))} 
                    disabled={page === 1}
                  >
                    <Icon name="arrow-left" size="20" />
                  </button>
                  <span className={styles.pagination}>
                    Page {page} sur {totalPages}
                  </span>
                  <button 
                    className={styles.arrow} 
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))} 
                    disabled={page === totalPages}
                  >
                    <Icon name="arrow-right" size="20" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Card>
      <Panel
        selectedIds={selectedIds}
        onDeleteSuccess={() => {
          setSelectedIds([]);
          fetchCenters();
        }}
      />
    </>
  );
};

export default CenterList;